import React from 'react';
import { StickyTabBar, TabItem, TAB_BAR_HEIGHT } from 'components/atomic/organisms/sticky-tab-bar';
import { useHeaderHeight } from 'hooks/use-header-height';
import { useIsNotMobile } from 'hooks/use-size-class';
import { typography, iconSizes } from 'design-system/tokens';
import { ListingSectionData } from 'hooks/use-listing-sections';
import { ListingSectionId } from 'domain/listing/types/listing-sections-config';
import {
  OverviewIcon,
  PriceHistoryIcon,
  HomeAppraisalIcon,
  MapIcon,
  SimilarListingsIcon,
  CommuteIcon,
} from './icons';

/**
 * Icon mapping for listing sections.
 * Note: Commute is kept in the enum but not used in sticky tabs.
 */
const LISTING_TAB_ICONS: Record<string, React.ComponentType<{ size: number }>> = {
  [ListingSectionId.OVERVIEW]: OverviewIcon,
  [ListingSectionId.PRICE_HISTORY]: PriceHistoryIcon,
  [ListingSectionId.HOME_APPRAISAL]: HomeAppraisalIcon,
  [ListingSectionId.MAP]: MapIcon,
  [ListingSectionId.SIMILAR_LISTINGS]: SimilarListingsIcon,
  // Commute icon kept for potential future use
  commute: CommuteIcon,
};

export interface StickyHeaderTabsProps {
  sections: ListingSectionData[];
  onTabClick?: (tabId: ListingSectionId) => void;
  disableSticky?: boolean;
  iconPosition?: 'start' | 'top' | 'end' | 'bottom';
  showIconsWhenSticky?: boolean;
  viewport?: 'desktop' | 'mobile';
}

/**
 * Sticky header tabs for listing pages.
 * 
 * Tab IDs must match the section element IDs in the DOM.
 * 
 * Analytics tracking should be handled by the parent component via onTabClick callback.
 */
export function StickyHeaderTabs({ 
  sections,
  onTabClick,
  disableSticky = false,
  iconPosition = 'start',
  showIconsWhenSticky = true,
  viewport = 'desktop',
}: StickyHeaderTabsProps) {
  const headerHeight = useHeaderHeight();
  const isNotMobile = useIsNotMobile();
  
  const fontSize = isNotMobile ? typography.fontSize.lg : typography.fontSize.md;
  const iconSize = isNotMobile ? parseInt(iconSizes.xxl) : parseInt(iconSizes.xl);

  const tabs: TabItem[] = sections.map(section => {
    const IconComponent = LISTING_TAB_ICONS[section.id];
    return {
      id: section.id,
      label: section.label,
      icon: IconComponent ? <IconComponent size={iconSize} /> : undefined,
    };
  });

  // Wrapper to convert string back to ListingSectionId for type-safe callback
  const handleTabClick = onTabClick 
    ? (tabId: string) => onTabClick(tabId as ListingSectionId)
    : undefined;

  return (
    <StickyTabBar
      tabs={tabs}
      onTabClick={handleTabClick}
      stickyTopOffset={headerHeight}
      showIconsWhenSticky={showIconsWhenSticky}
      disableSticky={disableSticky}
      iconPosition={iconPosition}
      fontSize={fontSize}
      iconSize={iconSize}
      viewport={viewport}
    />
  );
}

export { TAB_BAR_HEIGHT };
